import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, Download } from 'lucide-react';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import ScoreGauge from '@/components/ScoreGauge';
import RouteCard from '@/components/RouteCard';
import { api, API_BASE } from '@/lib/apiClient';

const MEMBERSHIP_URL = 'https://www.wearetheweave.co.uk/the-1-club';

export default function Results() {
    const { id } = useParams();
    const [result, setResult] = useState(null);
    const [error, setError] = useState('');

    useEffect(() => {
        api.get(`/submissions/${id}`)
            .then((r) => setResult(r.data))
            .catch((e) => setError(e?.response?.data?.detail || 'Could not load your result'));
    }, [id]);

    if (error) {
        return (
            <div className="min-h-screen bg-[#f8f4ec]">
                <Header />
                <div className="max-w-2xl mx-auto px-6 py-24 text-center">
                    <h1 className="font-display text-3xl text-weave-ink mb-4">Hmm, that didn't work</h1>
                    <p className="text-weave-ink-soft">{error}</p>
                </div>
            </div>
        );
    }
    if (!result) {
        return (
            <div className="min-h-screen bg-[#f8f4ec]">
                <Header />
                <div className="max-w-2xl mx-auto px-6 py-24 text-center text-weave-ink-soft">Loading…</div>
            </div>
        );
    }

    const pillars = [
        { label: 'Business structure', score: result.structure_score, signal: result.structure_signal, max: 25 },
        { label: 'Scalability', score: result.scalability_score, signal: result.scalability_signal, max: 25 },
        { label: 'Traction', score: result.traction_score, signal: result.traction_signal, max: 25 },
        { label: 'Funding goals', score: result.funding_goals_score, signal: result.funding_goals_signal, max: 25 },
        ...(result.crowdfunding_signal ? [{
            label: 'Crowdfunding readiness',
            score: result.crowdfunding_score || 0,
            signal: result.crowdfunding_signal,
            max: 10,
            secondary: true,
        }] : []),
    ];

    return (
        <div className="min-h-screen bg-[#f8f4ec] grain-bg">
            <Header />
            <main className="max-w-6xl mx-auto px-6 py-12" data-testid="results-page">
                <div className="grid md:grid-cols-12 gap-10 items-start">
                    <motion.div
                        initial={{ opacity: 0, y: 12 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="md:col-span-5 bg-white organic-border border border-weave-border p-8 flex flex-col items-center"
                    >
                        <div className="text-overline text-weave-teal mb-2">Your score</div>
                        <ScoreGauge score={result.total_funding_score} size={260} label={result.business_name} />
                        <div className="mt-8 w-full space-y-3 text-sm text-weave-ink-soft">
                            <Row label="Readiness band" value={result.readiness_band} testId="res-band" />
                            <Row label="Likely route" value={result.likely_route} testId="res-route" />
                        </div>
                        <a
                            href={`${API_BASE}/submissions/${result.id}/pdf`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="mt-6 w-full"
                            data-testid="download-pdf-btn"
                        >
                            <button className="pill-btn w-full bg-weave-yellow text-weave-ink hover:bg-weave-yellow-deep text-sm">
                                <Download className="w-4 h-4" /> Download PDF report
                            </button>
                        </a>
                    </motion.div>

                    <motion.div
                        initial={{ opacity: 0, y: 12 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.1 }}
                        className="md:col-span-7 space-y-5"
                    >
                        <div className="text-overline text-weave-teal">Headline read</div>
                        <h1 className="font-display text-4xl md:text-5xl text-weave-ink leading-[1.05] tracking-tight" data-testid="results-heading">
                            {result.readiness_band} · {result.likely_route}
                        </h1>
                        <div className="grid sm:grid-cols-2 gap-3">
                            <InsightCard title="Top strength" value={result.top_strength} color="#4aa0a4" />
                            <InsightCard title="Top gap" value={result.top_gap} color="#f4b847" />
                        </div>
                        <div className="bg-white organic-border border border-weave-border p-6">
                            <div className="text-overline text-weave-muted mb-2">Recommended next step</div>
                            <p className="text-weave-ink-soft leading-relaxed" data-testid="next-step">{result.recommended_next_step}</p>
                        </div>
                    </motion.div>
                </div>

                {/* Pillar breakdown */}
                <section className="mt-14">
                    <div className="text-overline text-weave-teal mb-3">Pillar breakdown</div>
                    <h2 className="font-display text-3xl text-weave-ink mb-6">Why this score</h2>
                    <div className="grid md:grid-cols-2 gap-4">
                        {pillars.map((p) => (
                            <div key={p.label} className="bg-white organic-border border border-weave-border p-6" data-testid={`pillar-result-${p.label.toLowerCase().replace(/ /g, '-')}`}>
                                <div className="flex justify-between items-baseline mb-3">
                                    <div className="font-display text-xl text-weave-ink">
                                        {p.label}
                                        {p.secondary && <span className="ml-2 text-overline text-weave-teal">supporting signal</span>}
                                    </div>
                                    <div className="font-mono text-sm text-weave-ink">{p.score} / {p.max}</div>
                                </div>
                                <div className="h-1.5 bg-weave-sand rounded-full mb-3 overflow-hidden">
                                    <motion.div
                                        initial={{ width: 0 }}
                                        animate={{ width: `${(p.score / p.max) * 100}%` }}
                                        transition={{ duration: 1, ease: 'easeOut' }}
                                        className="h-full bg-weave-teal rounded-full"
                                    />
                                </div>
                                <p className="text-sm text-weave-ink-soft leading-relaxed">{p.signal}</p>
                            </div>
                        ))}
                    </div>
                </section>

                {/* Route-specific actionable starter card */}
                <section className="mt-10">
                    <RouteCard route={result.likely_route} />
                </section>

                {/* What members get — non-misleading: this is a paid offer */}
                <section className="mt-10 bg-weave-ink text-weave-cream organic-border overflow-hidden" data-testid="member-offer">
                    <div className="grid md:grid-cols-12">
                        <div className="md:col-span-7 p-8 md:p-10">
                            <div className="text-overline text-weave-yellow mb-3">Take it further</div>
                            <h2 className="font-display text-3xl md:text-4xl leading-[1.05] mb-4">
                                Go deeper with The 1% Club.
                            </h2>
                            <p className="text-sm text-weave-cream/85 leading-relaxed mb-6 max-w-md">
                                Your diagnostic and interpretation are yours — free, on this
                                page and in the PDF. Membership unlocks the practical work:
                                regular funding clinics, a hand-curated scan of currently-available
                                funding for your business, and AI pitch-deck review.
                            </p>
                            <div className="flex flex-wrap gap-3">
                                <a href={MEMBERSHIP_URL} target="_blank" rel="noopener noreferrer" data-testid="results-become-member">
                                    <button className="pill-btn bg-weave-red text-white hover:bg-weave-red-deep text-sm">
                                        Become a member <ArrowRight className="w-4 h-4" />
                                    </button>
                                </a>
                            </div>
                            <p className="text-xs text-weave-cream/60 mt-3">
                                1% Club membership is paid — see plans on The Weave.
                            </p>
                        </div>
                        <ul className="md:col-span-5 bg-weave-ink-soft/40 p-8 md:p-10 space-y-4 text-sm text-weave-cream/95">
                            {[
                                ['Regular Funding Clinics', '1-hour sessions with a funding lead — book whenever you need.'],
                                ['Practical funding scans', 'Monthly hand-curated matches from the live UK funding database.'],
                                ['AI pitch-deck 4Cs review', 'Clarity · Conciseness · Cohesion · Compelling — with consent.'],
                            ].map(([title, desc]) => (
                                <li key={title} className="flex gap-3">
                                    <span className="w-1.5 h-1.5 mt-2 rounded-full bg-weave-yellow shrink-0" />
                                    <div>
                                        <div className="font-semibold text-weave-cream">{title}</div>
                                        <div className="text-weave-cream/70">{desc}</div>
                                    </div>
                                </li>
                            ))}
                        </ul>
                    </div>
                </section>
            </main>
            <Footer />
        </div>
    );
}

function Row({ label, value, testId }) {
    return (
        <div className="flex justify-between border-b border-weave-border pb-2">
            <span className="text-weave-muted">{label}</span>
            <span className="text-weave-ink font-medium" data-testid={testId}>{value}</span>
        </div>
    );
}

function InsightCard({ title, value, color }) {
    return (
        <div className="bg-white organic-border border border-weave-border p-5">
            <div className="text-overline mb-2" style={{ color }}>{title}</div>
            <div className="font-display text-xl text-weave-ink">{value}</div>
        </div>
    );
}
